import React, { useEffect, useRef, useState } from "react";

interface StreamingVideoProps {
  videoFile: string;
  vttFile?: string; // Added as an optional prop for future subtitle support
  onError?: () => void;
	autoPlay?: boolean; // Added to improve autoplay functionality
}

const StreamingVideo: React.FC<StreamingVideoProps> = ({ videoFile, vttFile, onError, autoPlay = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showControls, setShowControls] = useState(false);
	const [blobVttUrl, setBlobVttUrl] = useState<string>(""); // Added to help with CORS issues

  const handleError = () => {
    setError(true);
    if (onError) onError();
  };

  const getVideoUrl = (videoFile: string) => {
    const finalVideoUrl = `https://storage.googleapis.com/transparent-utah-large-video-bucket/${encodeURIComponent(videoFile)}`;
    return finalVideoUrl;
  };

  const getVttUrl = (vttFile: string) => {
    return `https://storage.googleapis.com/transparent-utah-large-video-bucket/${encodeURIComponent(vttFile)}`;
  }

	 // Added to Fix CORS Errors
  useEffect(() => {
    if (!vttFile) return;
    fetch(getVttUrl(vttFile))
      .then(res => res.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setBlobVttUrl(url);
      })
      .catch(() => {});

    return () => {
      if (blobVttUrl) {
        URL.revokeObjectURL(getVttUrl(vttFile));
      }
    };
  }, [vttFile]);

	// Updated useEffect
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();

    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();

            // Autoplay immediately when visible
            if (videoRef.current) {
              videoRef.current.muted = true;
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
            }
          }
        });
      },
      { threshold: 0.25 }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    // Force play immediately for hero/top-of-page videos
    if (videoRef.current) {
      videoRef.current.muted = true;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }

    return () => observer.disconnect();
  }, []);

  const handleTouch = () => {
    if (videoRef.current && isMobile) {
      setShowControls(!showControls);
    }
  };

  // Desktop: show controls on hover
  const handleMouseEnter = () => {
    if (videoRef.current && !isMobile) setShowControls(true);
  };
  const handleMouseLeave = () => {
    if (videoRef.current && !isMobile) setShowControls(false);
  };

  return (
    <section
      className="streaming-video-section position-relative overflow-hidden"
      style={{ backgroundColor: "#ffffff" }}
      ref={containerRef}
    >
      <div
        className="video-container"
        style={{ position: "relative", width: "100%", paddingBottom: "56.25%" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouch}
      >
        {error ? (
          <div
            className="bg-primary d-flex align-items-center justify-content-center"
            style={{ height: "100%" }}
          >
            <div className="text-center text-dark">
              <h1 className="display-4 fw-bold">Video Not Supported</h1>
              <p className="lead">Your browser doesn’t support HTML5 video.</p>
            </div>
          </div>
        ) : (
          isVisible && (
            <video
              ref={videoRef}
              // autoPlay
              // muted
							autoPlay={autoPlay} // Added to update autoPlay functionality
							muted={autoPlay} // Added to update autoPlay functionality
              loop
              playsInline
              preload="auto"
              // crossOrigin={vttFile ? "anonymous" : undefined} // Added to help with vtt caption file
              // controls={showControls}
							controls={showControls || !autoPlay}
              onError={handleError}
              onClick={() => videoRef.current?.play()}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                zIndex: 1,
              }}
            >
              <source src={getVideoUrl(videoFile)} type="video/mp4" />
              {/* Updated to fix CORS issues */}
              {blobVttUrl && (
                <track kind="captions" src={blobVttUrl} srcLang="en" label="English" default />
              )}
              Your browser doesn’t support HTML5 video.
            </video>
          )
        )}
      </div>
    </section>
  );
};

export default StreamingVideo;
